import React, { useState, useEffect, useRef } from "react";
import "./CAS4.css";

interface CAS4Props {
  isUnlocked?: boolean;
  onChatbotTrigger?: (visible: boolean) => void;
}

type MetricToggle = "psf" | "payback";
type SpaceType = "pod" | "conversion";

const LANDLORD_PROFILES = [
  {
    icon: "🏢",
    type: "WORKPLACE",
    headline: "Corporate & Office",
    image: "/images/IMG_5789.PNG",
    description:
      "UK law requires employers to provide suitable facilities for pregnant workers and nursing mothers under the Workplace (Health, Safety and Welfare) Regulations 1992, Reg 25. Toilets are explicitly not acceptable. A LOA pod is a turnkey compliance solution - private, lockable, purpose-built.",
    tags: ["Legal compliance", "Nursing mothers", "Equality Act 2010"],
  },
  {
    icon: "🏪",
    type: "VOID UNIT",
    headline: "Underused Retail",
    image: "/images/IMG_5794.PNG",
    description:
      "Sub-200sqft units that won't let on traditional terms. No inventory, no staffing, no operational burden. A managed concession that turns a balance sheet liability into a revenue-generating amenity.",
    tags: ["High street", "Retail park", "Shopping centre"],
  },
  {
    icon: "🏗️",
    type: "NEW BUILD",
    headline: "Development & Mixed-Use",
    image: "/images/IMG_5790.PNG",
    description:
      "Wellness infrastructure is now expected at planning stage. LOA delivers a credible, operational amenity that strengthens your scheme's amenity score and ESG credentials from day one.",
    tags: ["Mixed-use", "Residential above retail", "Amenity-led development"],
  },
];

const TAILWINDS = [
  {
    icon: "⚖️",
    stat: "Reg 25",
    title: "Regulatory Direction",
    front:
      "The Workplace (Health, Safety and Welfare) Regulations 1992 require employers to provide suitable rest facilities for pregnant and nursing workers. Toilets are not acceptable. The obligation exists - most buildings can't currently meet it.",
    back: "Under Regulation 25, all UK employers must provide suitable facilities for pregnant workers and nursing mothers to rest. HSE guidance specifies a private, hygienic room away from toilets, space to lie down if needed, and milk storage. The Equality Act 2010 additionally requires service providers not to discriminate against breastfeeding customers. In the US, the PUMP for Nursing Mothers Act (2022) extended equivalent federal protections to nearly 9 million additional workers. The compliance trajectory is clear in both markets - acting now positions your asset ahead of the curve.",
  },
  {
    icon: "📉",
    stat: "Rising",
    title: "Void Pressure",
    front:
      "Sub-200sqft units represent some of the hardest space to let in the current market. Traditional retail formats don't work at this scale. Managed concession models are filling the gap.",
    back: "UK high street vacancy rates range from 7% to over 19% depending on location, with sub-200sqft units among the most persistently empty - too small for standard retail, too niche for general letting. Meanwhile, managed concession models - coffee kiosks, ATMs, parcel lockers - have demonstrated that these spaces can generate consistent returns without traditional tenancy risk. LOA applies that same proven model to wellbeing infrastructure, a category with demonstrably growing demand.",
  },
  {
    icon: "🏆",
    stat: "Growing",
    title: "Amenity Expectation",
    front:
      "Wellness provision has moved from differentiator to expectation in commercial and mixed-use schemes. Buildings that can't demonstrate it are increasingly at a disadvantage in occupier conversations.",
    back: "Occupier surveys from major commercial agents consistently show wellness amenity rising in importance as a lease decision factor. BREEAM and WELL building standards - increasingly specified by institutional occupiers - both credit tangible wellness provision. For residential-led mixed-use schemes, wellness amenity is now routinely cited in marketing materials. The direction of travel is consistent: within a planning cycle, provision will be expected rather than noteworthy.",
  },
  {
    icon: "📊",
    stat: "ESG + Growth",
    title: "ESG & Wellness Demand",
    front:
      "Wellness is among the fastest-growing consumer categories globally. Landlords who provide it generate measurable social value, strengthen ESG reporting, and position their asset ahead of where occupier demand is heading.",
    back: "The Global Wellness Institute reports the wellness economy growing at approximately 10% annually. For institutional landlords, social value metrics are now standard in reporting frameworks - GRESB, BREEAM, and WELL all credit tangible community wellbeing provision. A LOA concession delivers ESG credentials that are genuinely difficult to generate cost-effectively: documented usage, measurable health outcomes, community access. Not a reporting checkbox - a physical asset with quantifiable impact.",
  },
];

// Base case assumptions — consistent with CAS3 growth model
const BASE_SESSIONS_PER_DAY = 10;
const PRICE_PER_SESSION = 20;
const OPERATING_DAYS = 347;
const LANDLORD_REVENUE_SHARE = 0.25; // 25%
const POD_COST = 15000; // manufacture & install
const CONVERSION_COST_PSF = 500;

function calculateMetrics(
  spaceType: SpaceType,
  sqft: number
): {
  fitout: number;
  revenuePsf: number;
  paybackMonths: number;
  podCount: number;
  annualLandlordRevenue: number;
} {
  // Pod purchase: fixed single pod regardless of sqft
  // Conversion: 150sqft+ can fit 2 pods, larger spaces 3
  const podCount =
    spaceType === "pod" ? 1 : sqft >= 180 ? 3 : sqft >= 150 ? 2 : 1;

  const fitout = spaceType === "pod" ? POD_COST : sqft * CONVERSION_COST_PSF;

  const annualGrossRevenue =
    podCount * BASE_SESSIONS_PER_DAY * PRICE_PER_SESSION * OPERATING_DAYS;
  const annualLandlordRevenue = annualGrossRevenue * LANDLORD_REVENUE_SHARE;
  const revenuePsf = Math.round(
    annualLandlordRevenue / (spaceType === "pod" ? 50 : sqft)
  );
  const paybackMonths = Math.round((fitout / annualLandlordRevenue) * 12);

  return { fitout, revenuePsf, paybackMonths, podCount, annualLandlordRevenue };
}

function formatCurrency(value: number): string {
  if (value >= 1000000) return "£" + (value / 1000000).toFixed(1) + "M";
  if (value >= 1000) return "£" + (value / 1000).toFixed(0) + "K";
  return "£" + value.toFixed(0);
}

// Floorplan zones — metrics aligned with base case assumptions
const FLOORPLAN_ZONES: Record<
  string,
  {
    title: string;
    type: string;
    description: string;
    metrics: { label: string; value: string }[];
  }
> = {
  pods: {
    title: "Reset Pods",
    type: "Core Revenue Driver",
    description:
      "Soundproofed individual pods for 20-minute reset sessions. App-based access, no staffing required. Each pod operates as an autonomous revenue unit on a 30-minute slot cycle.",
    metrics: [
      { label: "Sessions/day (base case)", value: "10 per pod" },
      { label: "Price per session", value: "£20" },
      { label: "Annual revenue per pod", value: "~£69K gross" },
      { label: "Staffing requirement", value: "Zero" },
    ],
  },
  reception: {
    title: "Entry & Transition",
    type: "Operational Zone",
    description:
      "App-based check-in with attended override. Minimal footprint. Creates the transition from public space to sanctuary - essential for the product to work effectively.",
    metrics: [
      { label: "Footprint", value: "30–60 sqft" },
      { label: "Staffing", value: "Unstaffed / remote monitored" },
      { label: "Access method", value: "App + contactless" },
      { label: "LOA responsibility", value: "Full" },
    ],
  },
  social: {
    title: "Social Space",
    type: "Dwell & Retention Zone",
    description:
      "Passive companionship area. Increases dwell time, drives repeat visits, and creates the community feel that converts single-session users into members.",
    metrics: [
      { label: "Footprint", value: "60–120 sqft" },
      { label: "Average dwell time", value: "~35 minutes total" },
      { label: "Staffing", value: "Zero" },
      { label: "LOA responsibility", value: "Full" },
    ],
  },
  kids: {
    title: "Kids Calm Zone",
    type: "Parent Market Enabler",
    description:
      "Sensory space for children while parents reset. Unlocks the parent market entirely - without this, parents can't use the pods. With it, you capture the most underserved demographic in urban wellness.",
    metrics: [
      { label: "Market unlocked", value: "Parents with 0–8yr children" },
      { label: "Footprint", value: "40–80 sqft" },
      { label: "Supervision", value: "Sight-line from social space" },
      { label: "Staffing", value: "Zero" },
    ],
  },
};

const CAS4: React.FC<CAS4Props> = ({ isUnlocked = true, onChatbotTrigger }) => {
  const [visibleSections, setVisibleSections] = useState<Set<string>>(
    new Set()
  );
  const [flippedCards, setFlippedCards] = useState<Set<number>>(new Set());
  const [metricToggle, setMetricToggle] = useState<MetricToggle>("payback");
  const [spaceType, setSpaceType] = useState<SpaceType>("pod");
  const [sqft, setSqft] = useState(50);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [activeLayout, setActiveLayout] = useState<"3pod" | "2pod">("3pod");

  const closeRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!isUnlocked) return;
    setVisibleSections(
      new Set([
        "setup",
        "platform",
        "profiles",
        "economics",
        "floorplan",
        "tailwinds",
        "close",
      ])
    );
  }, [isUnlocked]);

  useEffect(() => {
    if (!onChatbotTrigger) return;
    const caLane = document.querySelector(".ca-lane");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => onChatbotTrigger(entry.isIntersecting));
      },
      { threshold: 0.3, root: caLane }
    );
    if (closeRef.current) observer.observe(closeRef.current);
    return () => observer.disconnect();
  }, [onChatbotTrigger]);

  const metrics = calculateMetrics(spaceType, sqft);

  const handleZoneClick = (zoneId: string) => {
    setSelectedZone((prev) => (prev === zoneId ? null : zoneId));
  };

  if (!isUnlocked) {
    return (
      <div className="cas4-locked">
        <div className="lock-overlay">
          <div className="lock-content">
            <div className="lock-icon">🔒</div>
            <p>Complete the previous sections to unlock</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cas4-container">
      {/* Beat 01 — The Setup */}
      <section className="cas4-section cas4-setup" data-section-id="setup">
        <div className="setup-bg setup-bg-1" />
        <div className="setup-bg setup-bg-2" />
        <div className="setup-overlay" />
        <div
          className={`cas4-content ${
            visibleSections.has("setup") ? "visible" : ""
          }`}
        >
          <div className="beat-number">01</div>
          <h1>
            You've seen what it does.
            <br />
            <span className="coral">Now see what it does for you.</span>
          </h1>
          <p className="setup-sub">
            50–200 sqft. Managed entirely by LOA. Revenue shared with you. The
            managed concession model - already in your asset - applied to the
            fastest-growing category in commercial property.
          </p>
          <p className="setup-accent">
            You provide the space. We do everything else.
          </p>
        </div>
      </section>

      {/* Beat 02 — The Platform */}
      <section
        className="cas4-section cas4-platform"
        data-section-id="platform"
      >
        <div
          className={`cas4-content ${
            visibleSections.has("platform") ? "visible" : ""
          }`}
        >
          <div className="beat-number">02</div>
          <div className="cas4-label">THE MANAGEMENT AGREEMENT</div>
          <h2>
            A managed concession.
            <br />
            Standard terms. Zero hassle.
          </h2>
          <p className="platform-intro">
            The coffee kiosk in your lobby. The ATM by the entrance. The parcel
            locker in the car park. You didn't operate any of them. You provided
            space, took a revenue share, someone else ran it. LOA is that model
            applied to wellbeing infrastructure.
          </p>

          <div className="model-split">
            <div className="model-column">
              <div className="model-column-header">LANDLORD PROVIDES</div>
              <ul>
                <li>Space</li>
                <li>Fit-out capital</li>
                <li>Power & utilities connection</li>
                <li>Building access integration</li>
              </ul>
            </div>
            <div className="model-arrow">
              <div className="arrow-line" />
              <div className="arrow-center">Revenue Share</div>
              <div className="arrow-line" />
            </div>
            <div className="model-column">
              <div className="model-column-header">LOA MANAGES</div>
              <ul>
                <li>Brand, design & full fit-out spec</li>
                <li>Booking platform & technology</li>
                <li>Customer acquisition & marketing</li>
                <li>Day-to-day operations</li>
                <li>Cleaning, maintenance & equipment</li>
                <li>Insurance & liability</li>
              </ul>
            </div>
          </div>

          <div className="model-highlights">
            <div className="model-highlight">
              <span className="highlight-icon">📋</span>
              <span>Standard management agreement terms</span>
            </div>
            <div className="model-highlight">
              <span className="highlight-icon">🔍</span>
              <span>Transparent, auditable revenue share</span>
            </div>
            <div className="model-highlight">
              <span className="highlight-icon">✅</span>
              <span>Planning compliance, CSR & ESG credentials built in</span>
            </div>
          </div>
        </div>
      </section>

      {/* Beat 03 — Target Profiles */}
      <section
        className="cas4-section cas4-profiles"
        data-section-id="profiles"
      >
        <div
          className={`cas4-content ${
            visibleSections.has("profiles") ? "visible" : ""
          }`}
        >
          <div className="beat-number">03</div>
          <div className="cas4-label">WHERE LOA DEPLOYS</div>
          <h2>Three spaces. One model.</h2>
          <p className="profiles-sub">
            LOA targets spaces that are underperforming their potential - not
            prime retail competing with established tenants.
          </p>

          <div className="profiles-triptych">
            {LANDLORD_PROFILES.map((profile, i) => (
              <div key={i} className="profile-card">
                <div className="profile-image-wrap">
                  <img
                    src={profile.image}
                    alt={profile.headline}
                    className="profile-image"
                  />
                </div>
                <div className="profile-card-body">
                  <div className="profile-type">{profile.type}</div>
                  <h3>{profile.headline}</h3>
                  <p>{profile.description}</p>
                  <div className="profile-tags">
                    {profile.tags.map((tag, j) => (
                      <span key={j} className="profile-tag">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beat 04 — Unit Economics + Calculator */}
      <section
        className="cas4-section cas4-economics"
        data-section-id="economics"
      >
        <div
          className={`cas4-content ${
            visibleSections.has("economics") ? "visible" : ""
          }`}
        >
          <div className="beat-number">04</div>
          <div className="cas4-label">THE UNIT ECONOMICS</div>
          <h2>One pod. One machine.</h2>

          <div className="unit-economics">
            <div className="unit-card">
              <div className="unit-value">50–200</div>
              <div className="unit-metric">sqft required</div>
            </div>
            <div className="unit-card">
              <div className="unit-value">24</div>
              <div className="unit-metric">slots per day</div>
            </div>
            <div className="unit-card">
              <div className="unit-value">£20</div>
              <div className="unit-metric">per session</div>
            </div>
            <div className="unit-card highlight">
              <div className="unit-value">0</div>
              <div className="unit-metric">staff required</div>
            </div>
          </div>

          <div className="dual-strategy">
            <div className="strategy-card">
              <div className="strategy-label">BREATHE POD 50</div>
              <p>
                The entry unit. Single autonomous pod. Minimum footprint,
                maximum flexibility.
              </p>
            </div>
            <div className="strategy-divider">
              <span>scales to</span>
              <div className="divider-arrow">→</div>
            </div>
            <div className="strategy-card mission">
              <div className="strategy-label">BREATHE BAR 200</div>
              <p>
                Multi-pod. Kids calm zone. Social space. Community amenity at
                scale.
              </p>
            </div>
          </div>

          {/* Calculator */}
          <div className="calculator">
            <div className="calc-header">
              <div className="cas4-label">YOUR SPACE CALCULATOR</div>
              <div className="metric-toggle-group">
                <button
                  className={`metric-toggle-btn ${
                    metricToggle === "payback" ? "active" : ""
                  }`}
                  onClick={() => setMetricToggle("payback")}
                >
                  Payback Period
                </button>
                <button
                  className={`metric-toggle-btn ${
                    metricToggle === "psf" ? "active" : ""
                  }`}
                  onClick={() => setMetricToggle("psf")}
                >
                  Revenue / sqft
                </button>
              </div>
            </div>

            <div className="calc-inputs">
              <div className="calc-input-group">
                <label>Space type</label>
                <div className="space-type-toggle">
                  <button
                    className={spaceType === "pod" ? "active" : ""}
                    onClick={() => {
                      setSpaceType("pod");
                      setSqft(50);
                    }}
                  >
                    Pod Purchase
                  </button>
                  <button
                    className={spaceType === "conversion" ? "active" : ""}
                    onClick={() => setSpaceType("conversion")}
                  >
                    Unit Conversion
                  </button>
                </div>
              </div>

              {/* Sqft slider — only shown for unit conversion */}
              {spaceType === "conversion" && (
                <div className="calc-input-group">
                  <label>
                    Available sqft —{" "}
                    <span className="sqft-value">{sqft} sqft</span>
                    {metrics.podCount > 1 && (
                      <span className="pod-count-badge">
                        {metrics.podCount} pods possible
                      </span>
                    )}
                  </label>
                  <input
                    type="range"
                    min={50}
                    max={200}
                    step={10}
                    value={sqft}
                    onChange={(e) => setSqft(Number(e.target.value))}
                    className="sqft-slider"
                  />
                  <div className="slider-labels">
                    <span>50 sqft</span>
                    <span>200 sqft</span>
                  </div>
                </div>
              )}

              {/* Pod purchase — fixed spec callout */}
              {spaceType === "pod" && (
                <div className="pod-fixed-spec">
                  <span className="pod-spec-icon">📦</span>
                  <div>
                    <div className="pod-spec-title">
                      Single pod unit - fixed specification
                    </div>
                    <div className="pod-spec-sub">
                      50 sqft footprint · Manufactured & delivered by LOA · No
                      structural works required
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="calc-outputs">
              <div className="calc-output-card">
                <div className="calc-output-label">Estimated Fit-out</div>
                <div className="calc-output-value">
                  {formatCurrency(metrics.fitout)}
                </div>
                <div className="calc-output-sub">
                  {spaceType === "pod"
                    ? "Pod manufacture & install"
                    : `At £500/sqft · ${sqft}sqft · ${metrics.podCount} pod${
                        metrics.podCount > 1 ? "s" : ""
                      }`}
                </div>
              </div>
              <div className="calc-output-card">
                <div className="calc-output-label">Annual Landlord Revenue</div>
                <div className="calc-output-value">
                  {formatCurrency(metrics.annualLandlordRevenue)}
                </div>
                <div className="calc-output-sub">
                  25% revenue share · base case
                </div>
              </div>
              <div className="calc-output-card highlight span-2">
                <div className="calc-output-label">
                  {metricToggle === "payback"
                    ? "Payback Period"
                    : "Revenue / sqft / year"}
                </div>
                <div className="calc-output-value coral">
                  {metricToggle === "payback"
                    ? `${metrics.paybackMonths} months`
                    : `£${metrics.revenuePsf} / sqft`}
                </div>
                <div className="calc-output-sub">
                  {metricToggle === "payback"
                    ? "Fit-out recovered from landlord revenue share"
                    : "Landlord annual revenue share per sqft"}
                </div>
              </div>
            </div>

            <p className="calc-disclaimer">
              Indicative figures. Base case: 10 sessions/day at £20, 347
              operating days/year. Actual terms subject to management agreement.
            </p>
          </div>
        </div>
      </section>

      {/* Beat 05 — Floorplan */}
      <section
        className="cas4-section cas4-floorplan"
        data-section-id="floorplan"
      >
        <div
          className={`cas4-content cas4-content-wide ${
            visibleSections.has("floorplan") ? "visible" : ""
          }`}
        >
          <div className="beat-number">05</div>
          <div className="cas4-label">WHAT IT LOOKS LIKE BUILT OUT</div>
          <h2>The Breathe Bar 200.</h2>
          <p className="floorplan-sub">
            Click each zone to see operational detail and revenue metrics.
          </p>

          <div className="layout-toggle-dark">
            <button
              className={activeLayout === "3pod" ? "active" : ""}
              onClick={() => setActiveLayout("3pod")}
            >
              Three-Pod
            </button>
            <button
              className={activeLayout === "2pod" ? "active" : ""}
              onClick={() => setActiveLayout("2pod")}
            >
              Two-Pod
            </button>
          </div>

          <div className="floorplan-container">
            <div className="floorplan-svg-wrap">
              <svg viewBox="0 0 600 300" xmlns="http://www.w3.org/2000/svg">
                {/* 3-POD LAYOUT */}
                <g
                  style={{
                    display: activeLayout === "3pod" ? "block" : "none",
                  }}
                >
                  <rect
                    x="10"
                    y="10"
                    width="580"
                    height="280"
                    fill="none"
                    stroke="rgba(250,248,243,0.2)"
                    strokeWidth="2"
                  />
                  <g
                    style={{ cursor: "pointer" }}
                    onClick={() => handleZoneClick("pods")}
                    opacity={selectedZone && selectedZone !== "pods" ? 0.4 : 1}
                  >
                    <rect
                      x="30"
                      y="30"
                      width="170"
                      height="100"
                      fill={
                        selectedZone === "pods"
                          ? "rgba(232,84,79,0.3)"
                          : "rgba(232,84,79,0.15)"
                      }
                      stroke="#E8544F"
                      strokeWidth="1.5"
                    />
                    <rect
                      x="215"
                      y="30"
                      width="170"
                      height="100"
                      fill={
                        selectedZone === "pods"
                          ? "rgba(232,84,79,0.3)"
                          : "rgba(232,84,79,0.15)"
                      }
                      stroke="#E8544F"
                      strokeWidth="1.5"
                    />
                    <rect
                      x="400"
                      y="30"
                      width="170"
                      height="100"
                      fill={
                        selectedZone === "pods"
                          ? "rgba(232,84,79,0.3)"
                          : "rgba(232,84,79,0.15)"
                      }
                      stroke="#E8544F"
                      strokeWidth="1.5"
                    />
                    <text
                      x="115"
                      y="85"
                      style={{
                        fontFamily: "Outfit",
                        fontSize: 10,
                        fontWeight: 500,
                        fill: "rgba(250,248,243,0.7)",
                        pointerEvents: "none",
                        textAnchor: "middle",
                      }}
                    >
                      POD 1
                    </text>
                    <text
                      x="300"
                      y="85"
                      style={{
                        fontFamily: "Outfit",
                        fontSize: 10,
                        fontWeight: 500,
                        fill: "rgba(250,248,243,0.7)",
                        pointerEvents: "none",
                        textAnchor: "middle",
                      }}
                    >
                      POD 2
                    </text>
                    <text
                      x="485"
                      y="85"
                      style={{
                        fontFamily: "Outfit",
                        fontSize: 10,
                        fontWeight: 500,
                        fill: "rgba(250,248,243,0.7)",
                        pointerEvents: "none",
                        textAnchor: "middle",
                      }}
                    >
                      POD 3
                    </text>
                  </g>
                  <g
                    style={{ cursor: "pointer" }}
                    onClick={() => handleZoneClick("social")}
                    opacity={
                      selectedZone && selectedZone !== "social" ? 0.4 : 1
                    }
                  >
                    <rect
                      x="30"
                      y="150"
                      width="260"
                      height="120"
                      fill={
                        selectedZone === "social"
                          ? "rgba(250,248,243,0.12)"
                          : "rgba(250,248,243,0.04)"
                      }
                      stroke="rgba(250,248,243,0.3)"
                      strokeWidth="1.5"
                    />
                    <text
                      x="160"
                      y="215"
                      style={{
                        fontFamily: "Outfit",
                        fontSize: 10,
                        fontWeight: 500,
                        fill: "rgba(250,248,243,0.6)",
                        pointerEvents: "none",
                        textAnchor: "middle",
                      }}
                    >
                      SOCIAL SPACE
                    </text>
                  </g>
                  <g
                    style={{ cursor: "pointer" }}
                    onClick={() => handleZoneClick("kids")}
                    opacity={selectedZone && selectedZone !== "kids" ? 0.4 : 1}
                  >
                    <rect
                      x="310"
                      y="150"
                      width="180"
                      height="120"
                      fill={
                        selectedZone === "kids"
                          ? "rgba(74,139,110,0.2)"
                          : "rgba(74,139,110,0.06)"
                      }
                      stroke="rgba(74,139,110,0.5)"
                      strokeWidth="1.5"
                    />
                    <text
                      x="400"
                      y="215"
                      style={{
                        fontFamily: "Outfit",
                        fontSize: 10,
                        fontWeight: 500,
                        fill: "rgba(250,248,243,0.6)",
                        pointerEvents: "none",
                        textAnchor: "middle",
                      }}
                    >
                      KIDS CALM ZONE
                    </text>
                  </g>
                  <g
                    style={{ cursor: "pointer" }}
                    onClick={() => handleZoneClick("reception")}
                    opacity={
                      selectedZone && selectedZone !== "reception" ? 0.4 : 1
                    }
                  >
                    <rect
                      x="510"
                      y="150"
                      width="60"
                      height="120"
                      fill={
                        selectedZone === "reception"
                          ? "rgba(232,84,79,0.2)"
                          : "rgba(232,84,79,0.06)"
                      }
                      stroke="rgba(232,84,79,0.4)"
                      strokeWidth="1.5"
                    />
                    <text
                      x="540"
                      y="215"
                      style={{
                        fontFamily: "Outfit",
                        fontSize: 9,
                        fill: "rgba(250,248,243,0.6)",
                        pointerEvents: "none",
                        textAnchor: "middle",
                        transform: "rotate(-90deg)",
                        transformOrigin: "540px 215px",
                      }}
                    >
                      ENTRY
                    </text>
                  </g>
                </g>

                {/* 2-POD LAYOUT */}
                <g
                  style={{
                    display: activeLayout === "2pod" ? "block" : "none",
                  }}
                >
                  <rect
                    x="10"
                    y="10"
                    width="580"
                    height="280"
                    fill="none"
                    stroke="rgba(250,248,243,0.2)"
                    strokeWidth="2"
                  />
                  <g
                    style={{ cursor: "pointer" }}
                    onClick={() => handleZoneClick("pods")}
                    opacity={selectedZone && selectedZone !== "pods" ? 0.4 : 1}
                  >
                    <rect
                      x="60"
                      y="30"
                      width="210"
                      height="100"
                      fill={
                        selectedZone === "pods"
                          ? "rgba(232,84,79,0.3)"
                          : "rgba(232,84,79,0.15)"
                      }
                      stroke="#E8544F"
                      strokeWidth="1.5"
                    />
                    <rect
                      x="330"
                      y="30"
                      width="210"
                      height="100"
                      fill={
                        selectedZone === "pods"
                          ? "rgba(232,84,79,0.3)"
                          : "rgba(232,84,79,0.15)"
                      }
                      stroke="#E8544F"
                      strokeWidth="1.5"
                    />
                    <text
                      x="165"
                      y="85"
                      style={{
                        fontFamily: "Outfit",
                        fontSize: 10,
                        fontWeight: 500,
                        fill: "rgba(250,248,243,0.7)",
                        pointerEvents: "none",
                        textAnchor: "middle",
                      }}
                    >
                      POD 1
                    </text>
                    <text
                      x="435"
                      y="85"
                      style={{
                        fontFamily: "Outfit",
                        fontSize: 10,
                        fontWeight: 500,
                        fill: "rgba(250,248,243,0.7)",
                        pointerEvents: "none",
                        textAnchor: "middle",
                      }}
                    >
                      POD 2
                    </text>
                  </g>
                  <g
                    style={{ cursor: "pointer" }}
                    onClick={() => handleZoneClick("social")}
                    opacity={
                      selectedZone && selectedZone !== "social" ? 0.4 : 1
                    }
                  >
                    <rect
                      x="30"
                      y="150"
                      width="280"
                      height="120"
                      fill={
                        selectedZone === "social"
                          ? "rgba(250,248,243,0.12)"
                          : "rgba(250,248,243,0.04)"
                      }
                      stroke="rgba(250,248,243,0.3)"
                      strokeWidth="1.5"
                    />
                    <text
                      x="170"
                      y="215"
                      style={{
                        fontFamily: "Outfit",
                        fontSize: 10,
                        fontWeight: 500,
                        fill: "rgba(250,248,243,0.6)",
                        pointerEvents: "none",
                        textAnchor: "middle",
                      }}
                    >
                      SOCIAL SPACE
                    </text>
                  </g>
                  <g
                    style={{ cursor: "pointer" }}
                    onClick={() => handleZoneClick("kids")}
                    opacity={selectedZone && selectedZone !== "kids" ? 0.4 : 1}
                  >
                    <rect
                      x="330"
                      y="150"
                      width="180"
                      height="120"
                      fill={
                        selectedZone === "kids"
                          ? "rgba(74,139,110,0.2)"
                          : "rgba(74,139,110,0.06)"
                      }
                      stroke="rgba(74,139,110,0.5)"
                      strokeWidth="1.5"
                    />
                    <text
                      x="420"
                      y="215"
                      style={{
                        fontFamily: "Outfit",
                        fontSize: 10,
                        fontWeight: 500,
                        fill: "rgba(250,248,243,0.6)",
                        pointerEvents: "none",
                        textAnchor: "middle",
                      }}
                    >
                      KIDS CALM ZONE
                    </text>
                  </g>
                  <g
                    style={{ cursor: "pointer" }}
                    onClick={() => handleZoneClick("reception")}
                    opacity={
                      selectedZone && selectedZone !== "reception" ? 0.4 : 1
                    }
                  >
                    <rect
                      x="530"
                      y="150"
                      width="40"
                      height="120"
                      fill={
                        selectedZone === "reception"
                          ? "rgba(232,84,79,0.2)"
                          : "rgba(232,84,79,0.06)"
                      }
                      stroke="rgba(232,84,79,0.4)"
                      strokeWidth="1.5"
                    />
                    <text
                      x="550"
                      y="215"
                      style={{
                        fontFamily: "Outfit",
                        fontSize: 9,
                        fill: "rgba(250,248,243,0.6)",
                        pointerEvents: "none",
                        textAnchor: "middle",
                        transform: "rotate(-90deg)",
                        transformOrigin: "550px 215px",
                      }}
                    >
                      ENTRY
                    </text>
                  </g>
                </g>
              </svg>
            </div>

            {/* Detail Panel */}
            <div className="floorplan-detail">
              {selectedZone && FLOORPLAN_ZONES[selectedZone] ? (
                <>
                  <div className="detail-zone-type">
                    {FLOORPLAN_ZONES[selectedZone].type}
                  </div>
                  <h3>{FLOORPLAN_ZONES[selectedZone].title}</h3>
                  <p>{FLOORPLAN_ZONES[selectedZone].description}</p>
                  <div className="detail-metrics">
                    {FLOORPLAN_ZONES[selectedZone].metrics.map((m, i) => (
                      <div key={i} className="detail-metric">
                        <span className="detail-metric-label">{m.label}</span>
                        <span className="detail-metric-value">{m.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="detail-placeholder">
                  <p>
                    Select a zone to see operational detail and revenue metrics
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Beat 06 — Why Now */}
      <section
        className="cas4-section cas4-tailwinds"
        data-section-id="tailwinds"
      >
        <div
          className={`cas4-content ${
            visibleSections.has("tailwinds") ? "visible" : ""
          }`}
        >
          <div className="beat-number">06</div>
          <div className="cas4-label">WHY NOW</div>
          <h2>
            The market is moving.
            <br />
            <span className="coral">With or without you.</span>
          </h2>
          <p className="tailwinds-hint">Tap a card to flip</p>

          <div className="tailwinds-grid">
            {TAILWINDS.map((card, i) => (
              <div
                key={i}
                className={`tailwind-flip-container ${
                  flippedCards.has(i) ? "flipped" : ""
                }`}
                onClick={() => {
                  setFlippedCards((prev) => {
                    const next = new Set(prev);
                    if (next.has(i)) next.delete(i);
                    else next.add(i);
                    return next;
                  });
                }}
              >
                <div className="tailwind-flip-inner">
                  <div className="tailwind-front">
                    <div className="tailwind-icon">{card.icon}</div>
                    <div className="tailwind-stat">{card.stat}</div>
                    <h3>{card.title}</h3>
                    <p>{card.front}</p>
                  </div>
                  <div className="tailwind-back">
                    <p>{card.back}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beat 07 — The Close */}
      <section
        className="cas4-section cas4-close"
        data-section-id="close"
        ref={closeRef}
        style={{ backgroundImage: "url(/images/IMG_5795.PNG)" }}
      >
        <div className="close-overlay" />
        <div
          className={`cas4-content cas4-close-content ${
            visibleSections.has("close") ? "visible" : ""
          }`}
        >
          <div className="close-logo">LOA</div>
          <h2>
            The city takes.
            <br />
            <span className="coral">LOA gives back.</span>
          </h2>
          <p className="close-cta-text">
            If you have a space in mind, let's talk.
          </p>
          <a
            href="mailto:alex@tcglventures.com?subject=LOA%20%E2%80%94%20Location%20Partnership%20Enquiry"
            className="close-cta-button"
          >
            Book a conversation with Alex
          </a>
        </div>
      </section>
    </div>
  );
};

export default CAS4;
